/**
 * Resource Usage Service
 *
 * Handles fetching disk usage data and metadata from the resource-usage
 * Lambdas via the API Gateway. Requires an authenticated user with a valid
 * Auth0 access token.
 */

// Cached API base URL loaded from aws-exports.json
let API_BASE_URL = ""

/**
 * Load the resource-usage API base URL from aws-exports.json configuration.
 *
 * Uses VITE_API_URL env var if set, otherwise falls back to the apiUrl
 * from aws-exports.json, and finally to a relative path.
 *
 * @returns The base URL for resource-usage API endpoints (e.g. "https://host/api/resource-usage").
 */
async function loadApiBaseUrl(): Promise<string> {
  if (API_BASE_URL) {
    return API_BASE_URL
  }

  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    // VITE_API_URL env var takes precedence (used with npm run dev --host).
    // Falls back to apiUrl from aws-exports.json (CI-generated for deployed envs).
    // Last resort: relative path (will 404 locally but works behind CloudFront).
    const baseUrl: string = import.meta.env.VITE_API_URL || config.apiUrl || ""
    API_BASE_URL = baseUrl
      ? `${baseUrl}/api/resource-usage`
      : "/api/resource-usage"
    return API_BASE_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("Resource Usage API URL not configured")
  }
}

// ---------------------------------------------------------------------------
// Disk Usage Metadata
// ---------------------------------------------------------------------------

/**
 * Response shape from the GET /api/resource-usage/disk-usage-metadata endpoint.
 */
export interface DiskUsageMetadataResponse {
  /** The latest version (timestamp directory) for each scope. Null if no versions exist. */
  latest: Record<DiskUsageScope, string | null>
  /** Scopes that have an ncdu file available for the current user. */
  scopes_available: DiskUsageScope[]
  /** Admin-only: users with ncdu files per scope. Only present for admin users. */
  users_available?: Record<DiskUsageScope, string[]>
}

/**
 * Fetch disk usage metadata for the authenticated user.
 *
 * Returns the latest scan version per scope and which scopes have data
 * available for this user.
 *
 * @param accessToken - Auth0 access token for API authentication.
 * @returns The parsed metadata response.
 * @throws Error if the request fails or returns a non-OK status.
 */
export async function getDiskUsageMetadata(
  accessToken: string
): Promise<DiskUsageMetadataResponse> {
  const baseUrl = await loadApiBaseUrl()
  const url = `${baseUrl}/disk-usage-metadata`

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      errorData.error || `HTTP error! status: ${response.status}`
    )
  }

  const data: DiskUsageMetadataResponse = await response.json()
  return data
}

// ---------------------------------------------------------------------------
// Disk Usage
// ---------------------------------------------------------------------------

/**
 * A single item (file or directory) returned by the disk-usage API.
 */
export interface DiskUsageItem {
  /** Name of the file or directory. */
  name: string
  /** Type of the item: file, directory, excluded, error, or non-regular. */
  type: "file" | "directory" | "excluded" | "error" | "non-regular"
  /** Whether this item is a directory. */
  isDirectory: boolean
  /** Apparent size in bytes. */
  asize: number
  /** Disk size (allocated) in bytes. */
  dsize: number
  /** Cumulative apparent size (directories only). */
  cumasize?: number
  /** Cumulative disk size (directories only). */
  cumdsize?: number
  /** Number of items within (directories only). */
  items?: number
}

/**
 * Response shape from the GET /api/resource-usage/disk-usage endpoint.
 */
export interface DiskUsageResponse {
  /** The resolved path that was queried. */
  path: string
  /** The parent path for navigation, or null if at root. */
  parentPath: string | null
  /** Cumulative apparent size of the directory. */
  cumasize: number
  /** Cumulative disk size of the directory. */
  cumdsize: number
  /** Total number of items in the directory. */
  items: number
  /** List of child items sorted by dsize descending. */
  contents: DiskUsageItem[]
}

/**
 * Valid scope values for the disk-usage API.
 * Maps to different ncdu file locations on S3.
 */
export type DiskUsageScope = "userdata" | "wa_posit_homes" | "ea_posit_homes"

/**
 * Parameters for the getDiskUsage request.
 */
export interface GetDiskUsageParams {
  /** The scope to query (userdata, wa_posit_homes, ea_posit_homes). */
  scope: DiskUsageScope
  /** The version (timestamp directory) to read from. */
  version: string
  /** Optional path within the scope to list. Defaults to root "/". */
  path?: string
  /** Optional user override (admin only). If omitted, uses the JWT user. */
  user?: string
}

/**
 * Fetch disk usage data for the authenticated user.
 *
 * Calls GET /api/resource-usage/disk-usage with the specified scope, version,
 * and optional path. The user identity is derived from the JWT token by the Lambda.
 *
 * @param params - The query parameters (scope, version, and optional path).
 * @param accessToken - Auth0 access token for API authentication.
 * @returns The parsed disk usage response.
 * @throws Error if the request fails or returns a non-OK status.
 */
export async function getDiskUsage(
  params: GetDiskUsageParams,
  accessToken: string
): Promise<DiskUsageResponse> {
  const baseUrl = await loadApiBaseUrl()
  const url = `${baseUrl}/disk-usage`

  // Build query string
  const queryParams = new URLSearchParams()
  queryParams.set("scope", params.scope)
  queryParams.set("version", params.version)
  if (params.path) {
    queryParams.set("path", params.path)
  }
  if (params.user) {
    queryParams.set("user", params.user)
  }

  const fullUrl = `${url}?${queryParams.toString()}`

  const response = await fetch(fullUrl, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      errorData.error || `HTTP error! status: ${response.status}`
    )
  }

  const data: DiskUsageResponse = await response.json()
  return data
}

// ---------------------------------------------------------------------------
// Token Usage
// ---------------------------------------------------------------------------

/**
 * The three token counters returned per (day, agent) cell.
 */
export interface TokenCounts {
  /** Input tokens consumed. */
  inputTokens: number
  /** Output tokens generated. */
  outputTokens: number
  /** Total tokens (input + output). */
  totalTokens: number
}

/**
 * A single day's token usage, broken down per agent.
 *
 * The `agents` map contains an entry for every agent in the window's
 * `agents` list (zero-filled by the backend when the agent had no usage that
 * day), so callers can iterate the agent list without missing-key checks.
 */
export interface TokenUsageDay {
  /** ISO date (YYYY-MM-DD) in the caller's timezone. */
  date: string
  /** Per-agent token counts, keyed by canonical kebab-case agent ID. */
  agents: Record<string, TokenCounts>
}

/**
 * A single user's total token consumption for one agent over the window.
 *
 * Only present in the admin response's `topUsers`.
 */
export interface TopUser {
  /** The user identifier (sanitised Auth0 sub used as the table partition key). */
  user: string
  /**
   * The user's display name, resolved server-side from the users table. Falls
   * back to the sub when the user has no stored name.
   */
  name: string
  /** The user's total tokens for the agent over the window. */
  totalTokens: number
}

/**
 * Response shape from the token-usage endpoints.
 *
 * The weekly/today endpoints return everything except `topUsers`; the admin
 * weekly endpoint additionally populates `topUsers` (per-agent top consumers).
 */
export interface TokenUsageResponse {
  /** The IANA timezone the backend used to bucket days (echoed back). */
  timeZone: string
  /**
   * Whether the caller is a Resource Usage manager. Derived server-side from
   * the JWT groups claim; used to gate the admin view.
   */
  isAdmin: boolean
  /** The window's ISO dates, oldest first (today is last / rightmost). */
  days: string[]
  /** Every agent that had usage in the window, sorted alphabetically. */
  agents: string[]
  /** One entry per day in `days`, zero-filled for missing (day, agent) cells. */
  usage: TokenUsageDay[]
  /**
   * Admin only: top consuming users per agent over the window, keyed by agent
   * ID, each list sorted by total tokens descending. Absent on the non-admin
   * (weekly/today) endpoints.
   */
  topUsers?: Record<string, TopUser[]>
}

/**
 * Fetch token usage from one of the token-usage window endpoints.
 *
 * Shared implementation for the weekly and today endpoints, which return the
 * identical response shape and differ only in the window path segment.
 *
 * @param windowPath - The endpoint path segment: "weekly", "today", or
 *   "admin/weekly".
 * @param timeZone - The caller's IANA timezone (e.g. "Australia/Perth"), from
 *   Intl.DateTimeFormat().resolvedOptions().timeZone. Passed so the backend's
 *   day buckets line up with how usage was bucketed on write.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns The parsed token usage response.
 * @throws Error if the request fails or returns a non-OK status.
 */
async function fetchTokenUsage(
  windowPath: "weekly" | "today" | "admin/weekly",
  timeZone: string,
  accessToken: string
): Promise<TokenUsageResponse> {
  const baseUrl = await loadApiBaseUrl()

  const queryParams = new URLSearchParams()
  queryParams.set("timeZone", timeZone)

  const url = `${baseUrl}/token-usage/${windowPath}?${queryParams.toString()}`

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const data: TokenUsageResponse = await response.json()
  return data
}

/**
 * Fetch the authenticated user's token usage for the last seven days.
 *
 * @param timeZone - The caller's IANA timezone (e.g. "Australia/Perth").
 * @param accessToken - Auth0 access token for API authentication.
 * @returns The parsed token usage response (7 entries in `usage`).
 * @throws Error if the request fails or returns a non-OK status.
 */
export async function getWeeklyTokenUsage(
  timeZone: string,
  accessToken: string
): Promise<TokenUsageResponse> {
  return fetchTokenUsage("weekly", timeZone, accessToken)
}

/**
 * Fetch the authenticated user's token usage for today only.
 *
 * @param timeZone - The caller's IANA timezone (e.g. "Australia/Perth").
 * @param accessToken - Auth0 access token for API authentication.
 * @returns The parsed token usage response (1 entry in `usage`).
 * @throws Error if the request fails or returns a non-OK status.
 */
export async function getTodayTokenUsage(
  timeZone: string,
  accessToken: string
): Promise<TokenUsageResponse> {
  return fetchTokenUsage("today", timeZone, accessToken)
}

/**
 * Fetch token usage aggregated across all users for the last seven days.
 *
 * @param timeZone - The caller's IANA timezone (e.g. "Australia/Perth").
 * @param accessToken - Auth0 access token for API authentication.
 * @returns The parsed admin token usage response (7 entries in `usage`, plus
 *   `topUsers`).
 * @throws Error if the request fails, is forbidden, or returns a non-OK status.
 */
export async function getAdminWeeklyTokenUsage(
  timeZone: string,
  accessToken: string
): Promise<TokenUsageResponse> {
  return fetchTokenUsage("admin/weekly", timeZone, accessToken)
}
